import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import { Shimmer } from '@crowdex/ui/ai/shimmer';
import { ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ElementSelectionText } from '@/components/desktop-message-part/element-selection-text';
import {
  latestReplySteps,
  latestReplyText,
  turnActivity,
} from '@/lib/conversations/message-part/turn-activity';

/** How long a finished reply stays in view before it fades away. */
export const FLOATING_REPLY_FADE_MS = 8000;

/**
 * The agent's latest reply, floating as a bubble above the floating composer: its last words and
 * one line on what it did. It shows while the agent works and fades a few seconds after the
 * turn ends (not while hovered); focusing the composer brings it back. A reply already finished
 * when the conversation starts floating stays out of the way.
 */
export function FloatingReply({
  messages,
  status,
  onOpenConversation,
}: {
  messages: readonly CrowdexUIMessage[];
  status: 'submitted' | 'streaming' | 'ready' | 'error';
  /** Leaves the fullscreen view for the whole conversation. */
  onOpenConversation?: () => void;
}) {
  const working = status === 'submitted' || status === 'streaming';
  const [shown, setShown] = useState(working);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (working) setShown(true);
  }, [working]);

  useEffect(() => {
    if (working || !shown || hovered) return;
    const timer = window.setTimeout(() => setShown(false), FLOATING_REPLY_FADE_MS);
    return () => window.clearTimeout(timer);
  }, [working, shown, hovered]);

  const text = latestReplyText(messages);
  const steps = working ? null : latestReplySteps(messages);
  if (text === null && steps === null && !working) return null;

  return (
    <div
      className="desktop-floating-reply"
      data-shown={shown}
      // Reached while the composer has the focus, it stays once the focus moves into it.
      onPointerDownCapture={() => setShown(true)}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      <div className="desktop-floating-bubble">
        <div className="flex flex-col gap-2">
          {text === null ? (
            <Shimmer as="span" className="type-conversation">
              {turnActivity(messages)}
            </Shimmer>
          ) : (
            <ElementSelectionText isStreaming={status === 'streaming'} text={text} />
          )}
        </div>
      </div>
      <div className="desktop-floating-reply-meta">
        {working && text !== null && <Shimmer as="span">{turnActivity(messages)}</Shimmer>}
        {steps !== null && <span className="truncate">{steps}</span>}
        {onOpenConversation && (
          <button
            className="flex shrink-0 items-center gap-0.5 transition-colors hover:text-foreground"
            onClick={onOpenConversation}
            type="button"
          >
            Open conversation
            <ChevronRight className="size-3" />
          </button>
        )}
      </div>
    </div>
  );
}
